'use client';

import SvgChevronright from '@common/__generated__/icon/SvgChevronright';
import { SvgFireFill } from '@common/icon';
import { cn } from '@core/utils';

import { CustomLink } from '@/app/_components/CustomLink';
import { Rating } from '@/app/donation/_components/Rating';
import { Image } from '@/components/client';
import { BREAKPOINTS, DONATION_ROUTES } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface RecentOrderGiftListItemProps {
  mainImageUrl: string;
  institution: {
    logoUrl: string;
    id: number;
    name: string;
  };
  productNumber: string;
  productName: string;
  reviewCount: number;
  reviewRating: number;
  createAt: string;
}

export const RecentOrderGiftListItem = ({
  mainImageUrl,
  institution,
  productNumber,
  productName,
  reviewCount,
  reviewRating,
  createAt,
}: RecentOrderGiftListItemProps) => {
  const isDesktop = useMediaQuery(BREAKPOINTS.DESKTOP);
  const timeAgo = getTimeAgo(createAt);

  return (
    <li
      className={cn(
        'w-352 flex items-center justify-between gap-20',
        'mobile-tablet:w-[20.9375rem] mobile-tablet:justify-start mobile-tablet:gap-16 mobile-tablet:pr-32',
      )}
    >
      <CustomLink
        href={DONATION_ROUTES.GIFTS_DETAIL.pathname({
          productNumber,
        })}
        aria-label={`${productName} 상세 페이지로 이동`}
        className="relative size-fit rounded-xlarge"
      >
        <Image
          src={mainImageUrl}
          alt={productName}
          width={140 * 2}
          height={140 * 2}
          sizes={`${BREAKPOINTS.MOBILE_TABLET} 104px, 140px`}
          className={cn('size-140 rounded-xlarge', 'mobile-tablet:size-104')}
        />
      </CustomLink>
      <div className="flex flex-1 flex-col">
        <CustomLink
          className="flex items-center gap-4"
          href={DONATION_ROUTES.HOMETOWNS_DETAIL.pathname({
            id: institution.id,
          })}
        >
          <div
            className={cn(
              'relative size-24 overflow-hidden rounded-pill border-[1.2px] border-Gray-300',
              'mobile-tablet:size-20',
            )}
          >
            <Image
              fill
              src={institution.logoUrl}
              alt={`${institution.name} 로고 이미지`}
              className="object-cover"
              sizes={`${BREAKPOINTS.MOBILE_TABLET} 20px, 24px`}
            />
          </div>
          <p
            className={cn(
              'font-600 text-Gray-600 typo-body4',
              'mobile-tablet:font-600 mobile-tablet:typo-caption1',
            )}
          >
            {institution.name}
          </p>
          <SvgChevronright
            className={cn('size-16 text-Gray-600', 'mobile-tablet:size-12')}
          />
        </CustomLink>

        <CustomLink
          href={DONATION_ROUTES.GIFTS_DETAIL.pathname({
            productNumber,
          })}
          aria-label={`${productName} 상세 페이지로 이동`}
          className={cn(
            'line-clamp-2 pt-2 font-500 text-Gray-800 typo-body3',
            'mobile-tablet:font-500 mobile-tablet:typo-body4',
          )}
        >
          {productName}
        </CustomLink>

        <div className="flex items-center gap-4">
          <Rating value={reviewRating} size={isDesktop ? 16 : 12} />
          <span
            className={cn(
              'pt-6 font-400 text-Gray-600 typo-caption1',
              'mobile-tablet:font-400 mobile-tablet:typo-caption2',
            )}
          >
            ({reviewCount?.toLocaleString()})
          </span>
        </div>

        <div className="flex items-center pt-2">
          <SvgFireFill
            className={cn('size-20 text-O-300', 'mobile-tablet:size-14')}
          />
          <p
            className={cn(
              'whitespace-nowrap font-600 text-O-400 typo-body2',
              'mobile-tablet:font-600 mobile-tablet:typo-caption1',
            )}
          >
            {timeAgo} 전에 주문했어요!
          </p>
        </div>
      </div>
    </li>
  );
};

const getTimeAgo = (date: string) => {
  if (typeof date !== 'string') return '-';

  //* 현재 시간과 주어진 날짜를 사용자의 로컬 시간대에서 계산
  const now = new Date();
  //* 입력으로 받는 iso는 한국 시간이므로 국제 표준시(utc)로 변경하려면 offset을 붙여줘야 함
  const targetDate = new Date(date + '+09:00');

  //* 시간 차이 계산 (밀리초)
  const diff = now.getTime() - targetDate.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  //* 음수인 경우 (미래 날짜) 처리
  if (seconds < 0) {
    return '방금';
  }

  if (seconds < 60) {
    return `${seconds}초`;
  }

  if (minutes < 60) {
    return `${minutes}분`;
  }

  return `${hours}시간`;
};
